import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { forwardRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

/** Reemplazo de `<Radio.Group>` de Mantine (Radix UI) · agrupa `Radio`s con un solo `value`/`onValueChange`. */
export const RadioGroup = forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root ref={ref} className={cn("flex flex-col gap-2", className)} {...props} />
));
RadioGroup.displayName = "RadioGroup";

export interface RadioProps
  extends Omit<React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, "asChild"> {
  label?: ReactNode;
  description?: ReactNode;
}

/** Reemplazo de `<Radio>` de Mantine · usar dentro de `<RadioGroup>`. */
export const Radio = forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioProps>(
  ({ className, label, description, id, value, ...props }, ref) => {
    const autoId = id ?? `radio-${value}`;
    const dot = (
      <RadioGroupPrimitive.Item
        ref={ref}
        id={autoId}
        value={value}
        className={cn(
          "flex size-4 shrink-0 items-center justify-center rounded-full border border-input bg-background transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-multiva-600/30",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          "data-[state=checked]:border-multiva-600",
          className
        )}
        {...props}
      >
        <RadioGroupPrimitive.Indicator className="size-2 rounded-full bg-multiva-600" />
      </RadioGroupPrimitive.Item>
    );
    if (!label && !description) return dot;
    return (
      <div className="flex items-start gap-2">
        {dot}
        <div className="flex flex-col gap-0.5">
          {label && (
            <label htmlFor={autoId} className="cursor-pointer text-sm text-foreground">
              {label}
            </label>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </div>
      </div>
    );
  }
);
Radio.displayName = "Radio";
